import React, { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import {
  SignupFlowFooter,
  SignupFlowContent,
  SignupFlowWrapperContainer,
  FooterRight,
} from "../Components/SignupFlowWrapper.styles";
import { ChoiceWrapper } from "../Components/SignupContainer.styles";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
import RadioGroup from "components/RadioGroup/index";
import {
  updateFundingSourse,
  updateAffiliation,
} from "features/SurmountAccount/service";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { QuestionContent, QuestionContainer } from "./IntroQuestions.styles";
import { useNavigation } from "utils/navigate";
import {
  useLocalStorageSync,
  useCleanupOldKycData,
} from "hooks/useLocalStorage";
import {
  FUNDING_SOURCE_MAP,
  QuestionnaireStorageKeys,
  AFFILIATION_OPTIONS,
  FUNDING_SOURCE_OPTIONS,
} from "./constants";

const KycQuestions = () => {
  const dispatch = useDispatch();
  const {
    navigateToSignupSurmountAccountPhoneVerification,
    navigateToSignupIntroQuestions,
  } = useNavigation();

  // State management
  const [isSaving, setIsSaving] = useState(false);
  const [fundingSource, setFundingSource] = useState("");
  const [affiliations, setAffiliations] = useState("");

  // Clean up old localStorage data
  useCleanupOldKycData();

  // Sync state with localStorage
  useLocalStorageSync(
    QuestionnaireStorageKeys.FUNDING_SOURCE,
    fundingSource,
    setFundingSource,
    "",
    false
  );
  useLocalStorageSync(
    QuestionnaireStorageKeys.AFFILIATIONS,
    affiliations,
    setAffiliations,
    "",
    true
  );

  // Validation
  const isFormComplete = useMemo(() => {
    return fundingSource !== "";
  }, [fundingSource]);

  // Event handlers
  const handleBack = useCallback(() => {
    navigateToSignupSurmountAccountPhoneVerification();
  }, [navigateToSignupSurmountAccountPhoneVerification]);

  const handleContinue = useCallback(async () => {
    if (!isFormComplete) return;

    setIsSaving(true);
    try {
      // Prepare affiliations data
      let affiliationsData = null;
      // Only send data if an affiliation is selected (not empty string)
      if (affiliations && affiliations !== "") {
        affiliationsData = {
          is_control_person: affiliations === AFFILIATION_OPTIONS[0].value,
          is_affiliated_exchange_or_finra:
            affiliations === AFFILIATION_OPTIONS[1].value,
          is_politically_exposed: affiliations === AFFILIATION_OPTIONS[2].value,
          immediate_family_exposed:
            affiliations === AFFILIATION_OPTIONS[3].value,
        };
      }

      // Save data to backend
      await Promise.all(
        [
          fundingSource &&
            updateFundingSourse({
              funding_source:
                FUNDING_SOURCE_MAP[fundingSource] || fundingSource,
            }),
          affiliationsData && updateAffiliation(affiliationsData),
        ].filter(Boolean)
      );

      // Navigate to next step
      navigateToSignupIntroQuestions();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    isFormComplete,
    fundingSource,
    affiliations,
    dispatch,
    navigateToSignupIntroQuestions,
  ]);

  return (
    <SignupFlowWrapperContainer>
      <SignupFlowContent>
        <div className="header">
          <h3>Next, let&apos;s understand more about your investing habits</h3>
          <p>
            By answering the following questions, we can provide a tailored
            experience to help you succeed with your Surmount accounts!
          </p>
        </div>

        <QuestionContainer>
          {/* Funding Source */}
          <QuestionContent
            $isAnswered={!!fundingSource}
            $isCurrentOrPrevious={true}
            $isInteractive={true}
          >
            <ChoiceWrapper>
              <RadioGroup
                title="What will be your primary source of funding for this investment?"
                options={FUNDING_SOURCE_OPTIONS}
                value={fundingSource}
                onChange={setFundingSource}
                name="funding-source"
                disabled={false}
              />
            </ChoiceWrapper>
          </QuestionContent>

          {/* Affiliation */}
          <QuestionContent
            $isAnswered={true}
            $isCurrentOrPrevious={true}
            $isInteractive={true}
          >
            <ChoiceWrapper>
              <RadioGroup
                title="To comply with regulations, please select the one that applies (optional)"
                options={AFFILIATION_OPTIONS}
                value={affiliations}
                onChange={setAffiliations}
                name="affiliations"
                disabled={false}
              />
            </ChoiceWrapper>
          </QuestionContent>
        </QuestionContainer>
      </SignupFlowContent>

      <SignupFlowFooter>
        <FooterRight>
          <Button variant="secondary" onClick={handleBack} disabled={isSaving}>
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={isSaving || !isFormComplete}
          >
            {isSaving ? <Loader className="on-btn" /> : "Continue"}
          </Button>
        </FooterRight>
      </SignupFlowFooter>
    </SignupFlowWrapperContainer>
  );
};

export default KycQuestions;
